import React, { useState, useEffect, useRef } from "react";
import { FiAlignJustify } from "react-icons/fi";
import { ChevronsLeft, ChevronsRight, X } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
} from "@chakra-ui/modal";
import Card from "components/card";

export interface SidebarItem {
  path: string;
  label: string;
  icon: React.ReactNode;
}

interface SidebarProps {
  items: SidebarItem[];
  isActiveItems: (path: string) => boolean;
  ProfileSection: React.ReactNode;
  sidebarClassName?: string;
  isCollapsed?: boolean;
}

const SidebarContent: React.FC<{
  items: SidebarItem[];
  isActiveItems: (path: string) => boolean;
  ProfileSection: React.ReactNode;
  isCollapsed?: boolean;
}> = ({ items, isActiveItems, ProfileSection, isCollapsed }) => (
  <div className="flex flex-1 flex-col overflow-auto">
    {/* Profile Section */}
    <div
      className={`border-b border-[#ececec] px-6 pb-2 pt-4 dark:border-navy-700 ${
        isCollapsed ? "hidden" : ""
      }`}
    >
      {ProfileSection}
    </div>
    {/* Navigation Items */}
    <nav className="flex flex-1 flex-col gap-1 px-2 py-4">
      {items.map((item) => (
        <Link
          key={item.path}
          to={item.path}
          className={`
              flex items-center rounded-lg py-3 text-base font-medium transition-colors
              ${isCollapsed ? "justify-center px-3" : "gap-3 px-5"}
              ${
                isActiveItems(item.path)
                  ? "dark:bg-brand-900/20 border-l-4 border-brand-500 bg-brand-50 text-brand-600 dark:border-brand-400 dark:text-brand-400"
                  : "text-navy-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-navy-800"
              }
            `}
        >
          {item.icon}
          {!isCollapsed && <span>{item.label}</span>}
        </Link>
      ))}
    </nav>
  </div>
);

const Sidebar = ({
  items,
  isActiveItems,
  ProfileSection,
  sidebarClassName = "",
}: SidebarProps) => {
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const drawerContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1200) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
        setOpen(false); // Auto-close Drawer on desktop
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Prevent Drawer from opening on desktop
  useEffect(() => {
    if (!isMobile && open) setOpen(false);
  }, [isMobile, open]);

  // Implement click outside for mobile drawer
  useEffect(() => {
    if (!(isMobile && open)) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        drawerContentRef.current &&
        !drawerContentRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobile, open]);

  return (
    <>
      {/* Hamburger Menu Button - Only visible on mobile */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          isMobile && setOpen(!open);
        }}
        className="fixed bottom-4 right-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-brand-500 text-white shadow-lg transition-all hover:bg-brand-600 dark:bg-brand-400 dark:hover:bg-brand-500 lg:hidden"
      >
        {!open ? (
          <FiAlignJustify className="size-6" />
        ) : (
          <X className="size-6" />
        )}
      </button>

      {/* Sidebar for desktop */}
      <div
        className={`relative hidden transition-all duration-300 lg:static lg:block lg:h-auto lg:translate-x-0 lg:rounded-xl ${sidebarClassName} ${
          isCollapsed ? "lg:w-20" : "lg:w-[340px]"
        }`}
      >
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-4 top-1/2 z-10 hidden size-10 -translate-y-1/2 items-center justify-center rounded-full border bg-white text-gray-900 shadow-md hover:bg-gray-100 dark:border-navy-700 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700 lg:flex"
        >
          {isCollapsed ? (
            <ChevronsRight size={16} />
          ) : (
            <ChevronsLeft size={16} />
          )}
        </button>
        <Card
          extra="min-h-[84.7vh] "

          // className={`
          //   mr-4 flex min-h-[85vh]
          //   flex-col overflow-y-auto rounded-xl
          //   border-r border-gray-100
          //   bg-white
          //   shadow-md
          //   dark:border-navy-700
          //   dark:bg-navy-800
          // `}
        >
          <SidebarContent
            items={items}
            isActiveItems={isActiveItems}
            ProfileSection={ProfileSection}
            isCollapsed={isCollapsed}
          />
        </Card>
      </div>

      {/* Drawer for mobile */}
      <Drawer
        isOpen={isMobile && open}
        onClose={() => setOpen(false)}
        placement="left"
      >
        <div
          className={`
              bg-black/60 dark:bg-black/80 fixed inset-0 backdrop-blur-sm
              transition-opacity duration-300 ease-out
            `}
        />
        <DrawerContent
          ref={drawerContentRef}
          className="  max-w-[50vw] rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-navy-800 lg:hidden"
        >
          <DrawerHeader
            px="28px"
            w={{ base: "100%", md: "400px" }}
            pt="24px"
            pb="0px"
          >
            <DrawerCloseButton className="absolute right-[26px] top-[26px] h-4 w-4 text-gray-900 dark:text-white" />
            <p className="text-xl font-bold text-gray-900 dark:text-white">
              Menu
            </p>
          </DrawerHeader>
          <DrawerBody
            className={`
              flex
              min-h-screen flex-col
              bg-white
              p-0 dark:bg-navy-800
            `}
          >
            <div
              className={`
                flex h-full flex-col
                overflow-y-auto rounded-xl border-r
                border-gray-100 bg-white
                shadow-md
                dark:border-navy-700
                dark:bg-navy-800
              `}
            >
              <SidebarContent
                items={items}
                isActiveItems={isActiveItems}
                ProfileSection={ProfileSection}
              />
            </div>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default Sidebar;
